import styled from '@emotion/styled';

import { Badge } from '@/components/common/Badge';
import { screen } from '@/components/common/breakpoints';
import { CaptionHeadingDescription } from '@/components/common/CaptionHeadingDescription';
import { Icon } from '@/components/common/Icon';
import { Image } from '@/components/common/image';
import { HeadingLevel } from '@/components/common/MarkUp';
import { Section } from '@/components/layout/Section';
import { bodyLarge } from '@/styles/typography';

import type {
  SurfaceColourTokenType,
  TextColourTokenType,
} from '@/types/colours';
import type { BlockWithAudience } from '@/types/shared';
import type { DecorativeColourToken, Timeline as SanityTimeline } from '@/sanity/sanity.types'

export interface TimelineProps extends SanityTimeline, BlockWithAudience { }

export const Timeline = ({
  tag,
  caption,
  heading,
  subheading,
  items,
  image,
  colour,
  isCaptionUppercase,
  backgroundColour,
  blockSpacing,
  paddingAdjustment,
  pageAudience,
  isAudienceSwitcherEnabled,
}: TimelineProps) => {
  const mainColour = colour?.token;
  const getColourTokens = (
    backgroundColourToken,
  ): {
    textColour: TextColourTokenType;
    headingColour: TextColourTokenType;
  } => {
    switch (backgroundColourToken) {
      case '--surface-primary-subtle':
        return {
          textColour: '--text-strong',
          headingColour: '--text-action',
        };
      case '--surface-inverse-subtle':
        return {
          textColour: '--text-eclipse',
          headingColour: '--text-eclipse',
        };
      case '--surface-inverse-primary':
        return {
          textColour: '--text-inverse-strong',
          headingColour: '--text-inverse-strong',
        };
      case '--surface-danger-strong':
        return {
          textColour: '--text-inverse-strong',
          headingColour: '--text-inverse-strong',
        };
      case '--surface-primary':
        return {
          textColour: '--text-inverse-strong',
          headingColour: '--text-inverse-strong',
        };
      case '--surface-eclipse':
        return {
          textColour: '--text-strong',
          headingColour: '--text-strong',
        };
      case '--surface-subtle':
        return {
          textColour: '--text-warm-base',
          headingColour: '--text-strong',
        };
      case '--surface-warm-base':
        return {
          textColour: '--text-strong',
          headingColour: '--text-strong',
        };
      case '--surface-cool-strong':
        return {
          textColour: '--text-warm-base',
          headingColour: '--text-strong',
        };
      default:
        return {
          textColour: '--text-warm-base',
          headingColour: '--text-strong',
        };
    }
  };
  const backgroundColourToken =
    backgroundColour?.token || '--background-subtle';
  return (
    <Section
      verticalPadding={paddingAdjustment ?? true}
      spacing={blockSpacing}
      backgroundColour={backgroundColourToken}
      className="timeline"
    >
      <HeadingLevel>
        {tag?.tagText && (
          <Badge
            bgColour={tag?.tagColour}
            margin="0 0 var(--spacing-xxx-small) 0"
          >
            {tag.tagText}
          </Badge>
        )}
        <StyledCaptionHeadingDescription
          caption={caption}
          heading={heading}
          description={subheading}
          isCaptionUppercase={isCaptionUppercase}
        />
        <OuterWrapper>
          {image && image.asset?._ref && (
            <ImageWrapper>
              <StyledImage
                objectFit="cover"
                sizes="(max-width: 959px) 0px, 40%"
                radius="l"
                {...image}
              />
            </ImageWrapper>
          )}
          <CardsWrapper>
            {items.map((item) => {
              const backgroundColourToken =
                item.backgroundColour?.token || '--surface-cool-strong';
              const { textColour, headingColour } = getColourTokens(
                backgroundColourToken,
              );
              return (
                <Card key={item._key} colour={mainColour}>
                  <StyledIcon
                    icon="Hexagon"
                    colour={mainColour}
                    aria-hidden
                    size="20px"
                  />

                  <CardContainer
                    backgroundColour={backgroundColourToken}
                    textColour={textColour}
                    headingColour={headingColour}
                  >
                    <HeadingLevel>
                      <CaptionHeadingDescription
                        heading={item.itemHeading}
                        description={item.itemBody}
                        padding={{
                          description: 'xxx-small',
                          descriptionMobile: 'xxx-small',
                        }}
                        defaultHeadingSize="subheading-sm"
                        pageAudience={pageAudience}
                        isAudienceSwitcherEnabled={isAudienceSwitcherEnabled}
                      />
                    </HeadingLevel>
                  </CardContainer>
                </Card>
              );
            })}
          </CardsWrapper>
        </OuterWrapper>
      </HeadingLevel>
    </Section>
  );
};

const OuterWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-300);
  padding-top: var(--spacing-300);
  ${screen.md} {
    flex-direction: row;
  }
`;

const ImageWrapper = styled.div`
  width: 100%;
  max-width: 40%;
  max-height: 517px;
  display: none;

  ${screen.md} {
    display: block;
    > div {
      height: 100%;
      max-height: 380px;
    }
  }

  ${screen.lg} {
    > div {
      max-height: 517px;
    }
  }
`;

const CardsWrapper = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: var(--spacing-300);
  ${screen.sm} {
    gap: 80px;
  }
`;

const StyledCaptionHeadingDescription = styled(CaptionHeadingDescription)`
  margin-bottom: var(--spacing-x-large);
`;

const CardContainer = styled.div<{
  backgroundColour?: SurfaceColourTokenType;
  textColour?: TextColourTokenType;
  headingColour?: TextColourTokenType;
}>`
  background-color: ${(p) => `var(${p.backgroundColour})`};
  color: ${(p) => `var(${p.textColour})`};
  border-radius: var(--spacing-x-small);
  padding: var(--spacing-250);
  margin-left: calc(16px + var(--spacing-xx-small)); // 16px is Hexagon width
  box-shadow: var(--shadow-subtle);

  h3 {
    color: ${(p) => `var(${p.headingColour})`};
  }
  p,
  li {
    ${bodyLarge}
  }

  li {
    margin-left: var(--spacing-250);
    margin-bottom: var(--spacing-xx-small);
    list-style-type: disc;
  }
`;

const Card = styled.div<{ colour: DecorativeColourToken }>`
  position: relative;

  &:not(:last-child) {
    &::before {
      content: '';
      position: absolute;
      width: 1px;
      background-color: ${({ colour }) => `var(${colour})`};
      height: calc(100% + var(--spacing-300));
      left: 8px;
      top: var(--spacing-400);
    }
  }

  svg path {
    fill: ${({ colour }) => `var(${colour})`};
  }

  ${screen.sm} {
    &:not(:last-child) {
      &::before {
        height: calc(100% + 80px);
      }
    }
  }
`;

const StyledImage = styled(Image)`
  box-shadow: var(--shadow-base);

  height: 100%;
`;

const StyledIcon = styled(Icon)`
  top: var(--spacing-250);
  position: absolute;

  left: -1.3px;
`;
